import { cn } from "lib/utils"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { trpc } from "@/lib/trpc"

interface LogoProps {
  className?: string
  width?: string
  alt?: string
}

export function Logo({ className, width, alt }: LogoProps) {
  const { t } = useTranslation()
  const defaultSrc = "/images/logo-colour.svg"
  const [failedIndices, setFailedIndices] = useState<number[]>([])

  const { data: professionalBrandLogos } = trpc.users.listProfessionalBrandLogos.useQuery()
  const logos = professionalBrandLogos?.logos ?? []
  const totalCount = professionalBrandLogos?.totalCount ?? 0

  const logoAlt = alt || t("app.logoAlt")
  const visibleLogos = useMemo(() => logos.slice(0, 4), [logos])
  const overflowCount = Math.max(totalCount - visibleLogos.length, 0)

  if (visibleLogos.length === 0) {
    return (
      <div className={cn("px-6 md:px-4", className)}>
        <img src={defaultSrc} alt={logoAlt} className={cn(width)} />
      </div>
    )
  }

  if (visibleLogos.length === 1) {
    const logoSrc = failedIndices.includes(0) ? defaultSrc : (visibleLogos[0] ?? defaultSrc)
    return (
      <div className={cn("px-6 md:px-4", className)}>
        <img
          src={logoSrc}
          alt={logoAlt}
          className={cn(width)}
          onError={() => {
            setFailedIndices((current) => (current.includes(0) ? current : [...current, 0]))
          }}
        />
      </div>
    )
  }

  return (
    <div className={cn("px-6 md:px-4", className)}>
      <div className="flex items-center">
        {visibleLogos.map((logo, index) => {
          const src = failedIndices.includes(index) ? defaultSrc : logo
          return (
            <img
              key={logo}
              src={src}
              alt={`Estate logo ${index + 1}`}
              className={cn(
                "h-8 w-8 rounded-full border border-border bg-background object-cover",
                index > 0 ? "-ml-2" : "",
              )}
              onError={() => {
                setFailedIndices((current) =>
                  current.includes(index) ? current : [...current, index],
                )
              }}
            />
          )
        })}
        {overflowCount > 0 ? (
          <span className="ml-2 text-xs font-medium text-muted-foreground">+{overflowCount}</span>
        ) : null}
      </div>
    </div>
  )
}
